import * as React from 'react';
import './Panel.css';

interface PanelProps {
    title?: string;
    /** A domain hue, e.g. 'var(--domain-crashes)'. Tints the header chip and rule. */
    accent?: string;
    /** Small mark shown in the accent chip beside the title. */
    icon?: React.ReactNode;
    subtitle?: string;
    /** Renders the round "i" button in the header; omit for a plain panel. */
    onInfo?: () => void;
    infoLabel?: string;
    /** Arbitrary header controls, right-aligned before the info button. */
    actions?: React.ReactNode;
    footer?: React.ReactNode;
    className?: string;
    children: React.ReactNode;
}

/**
 * The card chrome every dashboard block shares: one surface, one hairline
 * border, one radius, one shadow. Replaces the per-card header styling that
 * used to be duplicated across the cards.
 */
const Panel: React.FC<PanelProps> = (props: PanelProps) => (
    <section
        className={`panel has-accent${props.accent ? ' panel--accented' : ''}${props.className ? ` ${props.className}` : ''}`}
        style={props.accent ? ({'--accent': props.accent} as React.CSSProperties) : undefined}
    >
        {(props.title || props.onInfo) && (
            <header className="panel__header">
                {props.icon && <span className="panel__chip" aria-hidden="true">{props.icon}</span>}
                <div>
                    {props.title && <h2 className="panel__title">{props.title}</h2>}
                    {props.subtitle && <p className="panel__subtitle">{props.subtitle}</p>}
                </div>
                {props.actions && <div className="panel__actions">{props.actions}</div>}
                {props.onInfo && (
                    <button
                        type="button"
                        className="panel__action"
                        onClick={props.onInfo}
                        aria-label={props.infoLabel ?? `About ${props.title ?? 'this panel'}`}
                    >
                        i
                    </button>
                )}
            </header>
        )}
        <div className="panel__body">{props.children}</div>
        {props.footer && <div className="panel__footer">{props.footer}</div>}
    </section>
);

export default Panel;
